import { z } from "zod";

import type { ContentBlock, ContentBlockKind, ContentTag } from "./types";
import type { ContentRelationship, RelationshipType } from "./relationships";

/**
 * Runtime shape of the editorial content shipped as JSON under `./data`.
 *
 * The JSON is generated from `types.ts` by
 * `scripts/content/convert-content-to-json.mjs`, so this schema validates what
 * the registry itself relies on — the identity, index and edge fields — rather
 * than restating the per-kind body unions. Bodies stay typed by `ContentBody<K>`
 * at the read boundary; duplicating those 700 lines here would only create two
 * definitions to drift apart.
 */

const CONTENT_BLOCK_KINDS = [
  "info",
  "checklist",
  "data-point",
  "test",
  "tip",
  "faq",
  "cost-item",
  "supplement",
  "timeline-step",
  "evidence",
  "guide",
  "comparison",
  "callout",
  "product",
  "article",
  "program",
  "procedure",
  "module",
  "therapy",
  "medication",
  "condition",
] as const;

const CONTENT_TAGS = [
  "egg-freezing",
  "embryo-freezing",
  "sperm",
  "testing",
  "amh",
  "afc",
  "genetic-screening",
  "semen-analysis",
  "supplements",
  "lifestyle",
  "optimization",
  "cost",
  "insurance",
  "clinic",
  "evidence-based",
  "expert-reviewed",
  "her",
  "his",
  "both-partners",
  "at-home",
  "lab",
  "clinic-visit",
  "pre-cycle",
  "during-cycle",
  "post-cycle",
  "pmos",
  "pcos",
  "endo",
  "thyroid",
  "age-factor",
  "medications",
  "emotional",
  "practical",
  "recovery",
  "storage",
  "travel",
  "moon",
  "moon-guides",
  "moon-decisions",
  "moon-science",
  "moon-costs",
  "moon-spotlight",
  "module",
  "assessment",
  "treatment",
  "iui",
  "ivf",
  "fet",
  "couples",
  "genomic",
  "pharmacogenomics",
  "metabolic",
  "autoimmune",
  "nutritional",
] as const;

const RELATIONSHIP_TYPES = [
  "contains",
  "related-to",
  "prerequisite-of",
  "deeper-dive",
  "data-for",
  "produces",
  "offers",
  "alternative-to",
] as const;

/**
 * Both directions must hold, so adding a kind/tag/relationship type to
 * `types.ts` without adding it here is a compile error rather than a
 * validation failure that only shows up when the new content loads.
 */
type MutuallyExhaustive<A extends string, B extends string> = [
  Exclude<A, B>,
  Exclude<B, A>,
] extends [never, never]
  ? true
  : never;

export const _kindsExhaustive: MutuallyExhaustive<
  ContentBlockKind,
  (typeof CONTENT_BLOCK_KINDS)[number]
> = true;
export const _tagsExhaustive: MutuallyExhaustive<
  ContentTag,
  (typeof CONTENT_TAGS)[number]
> = true;
export const _relationshipTypesExhaustive: MutuallyExhaustive<
  RelationshipType,
  (typeof RELATIONSHIP_TYPES)[number]
> = true;

const contentBlockSchema = z
  .object({
    id: z.string().min(1),
    kind: z.enum(CONTENT_BLOCK_KINDS),
    tags: z.array(z.enum(CONTENT_TAGS)),
    title: z.string(),
    body: z.record(z.string(), z.unknown()),
  })
  .loose();

const contentRelationshipSchema = z
  .object({
    from: z.string().min(1),
    to: z.string().min(1),
    type: z.enum(RELATIONSHIP_TYPES),
    label: z.string().optional(),
  })
  .loose();

/**
 * Strict on purpose. `applyLocale` in `registry.ts` spreads a variant over the
 * base block, so an unexpected key here — `id`, `kind`, `tags` — would produce a
 * block whose identity disagrees with the indexes it is filed under. These are
 * exactly the three fields the registry's own `LocaleVariant` type allows.
 */
const localeVariantEntrySchema = z.object({
  id: z.string().min(1),
  locale: z.string().min(1),
  variant: z.strictObject({
    title: z.string().optional(),
    subtitle: z.string().optional(),
    body: z.record(z.string(), z.unknown()).optional(),
  }),
});

export const contentDataFileSchema = z.object({
  blocks: z.array(contentBlockSchema),
  relationships: z.array(contentRelationshipSchema),
  localeVariants: z.array(localeVariantEntrySchema),
});

export type ContentDataFile = {
  blocks: ContentBlock[];
  relationships: ContentRelationship[];
  localeVariants: {
    id: string;
    locale: string;
    variant: { title?: string; subtitle?: string; body?: unknown };
  }[];
};
